import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Plus, Trash2, Settings2, Loader2, Users, PieChart, Info, ChevronDown, ChevronRight,
  DollarSign, Percent, Award, HelpCircle, TrendingDown, Layers, RefreshCw,
  FileText, Clock, Building2, Shield, ArrowUpDown, Briefcase, Target,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

const API = '/api/finance/cap-table';

// ==================== FORMATTERS ====================
const fmtM = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '-';
  const s = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${s}$${(a / 1e3).toFixed(0)}K`;
  return `${s}$${a.toFixed(0)}`;
};
const fmtN = (v) => (Number(v) || 0).toLocaleString('en-US');
const fmtPct = (v) => `${(Number(v) || 0).toFixed(2)}%`;
const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

// ==================== CONSTANTS ====================
const HOLDER_TYPES = [
  { value: 'founder', label: 'Founder' },
  { value: 'individual_investor', label: 'Individual Investor' },
  { value: 'institutional_investor', label: 'Institutional Investor' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'employee', label: 'Employee' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'esop_pool', label: 'ESOP Pool' },
  { value: 'treasury', label: 'Treasury / Reserve' },
  { value: 'nominee', label: 'Nominee' },
];

const INSTRUMENT_TYPES = [
  { value: 'common_share', label: 'Common Shares' },
  { value: 'preferred_share', label: 'Preferred Shares' },
  { value: 'founder_share', label: 'Founder Shares' },
  { value: 'advisory_share', label: 'Advisory Shares' },
  { value: 'option', label: 'Options (ESOP)' },
  { value: 'rsu', label: 'RSU' },
  { value: 'phantom_equity', label: 'Phantom Equity' },
  { value: 'safe', label: 'SAFE' },
  { value: 'convertible_note', label: 'Convertible Note' },
  { value: 'warrant', label: 'Warrant' },
  { value: 'pending_subscription', label: 'Pending Subscription' },
];

const INSTRUMENT_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'indicative', label: 'Indicative' },
  { value: 'approved', label: 'Approved' },
  { value: 'executed', label: 'Executed' },
  { value: 'funded', label: 'Funded' },
  { value: 'issued', label: 'Issued' },
  { value: 'active', label: 'Active' },
  { value: 'converted', label: 'Converted' },
  { value: 'exercised', label: 'Exercised' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rescinded', label: 'Rescinded' },
  { value: 'returned', label: 'Returned' },
  { value: 'expired', label: 'Expired' },
];

const ROUND_TYPES = [
  { value: 'safe', label: 'SAFE' },
  { value: 'priced_equity', label: 'Priced Equity' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'convertible_note', label: 'Convertible Note' },
];

const ROUND_STATUSES = [
  { value: 'indicative', label: 'Indicative' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const GRANT_STATUSES = [
  { value: 'indicative', label: 'Indicative' },
  { value: 'approved', label: 'Approved' },
  { value: 'granted', label: 'Granted' },
  { value: 'vested', label: 'Vested' },
  { value: 'exercised', label: 'Exercised' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'lapsed', label: 'Lapsed' },
];

const COMMITMENT_STATUSES = [
  { value: 'target', label: 'Target' },
  { value: 'in_discussion', label: 'In Discussion' },
  { value: 'soft_circled', label: 'Soft Circled' },
  { value: 'committed', label: 'Committed' },
  { value: 'docs_signed', label: 'Docs Signed' },
  { value: 'funded', label: 'Funded' },
  { value: 'issued', label: 'Issued' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const INST_TYPE_COLORS = {
  common_share: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  preferred_share: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  founder_share: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  advisory_share: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
  option: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  rsu: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
  phantom_equity: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  safe: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  convertible_note: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  warrant: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  pending_subscription: 'bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-300',
};

const HOLDER_TYPE_COLORS = {
  founder: 'bg-blue-500', individual_investor: 'bg-emerald-500',
  institutional_investor: 'bg-teal-500', advisor: 'bg-cyan-500',
  employee: 'bg-amber-500', contractor: 'bg-orange-500',
  esop_pool: 'bg-violet-500', treasury: 'bg-slate-500', nominee: 'bg-gray-500',
};

// ==================== HELPER UI COMPONENTS ====================
function HelpTip({ text, side = 'top' }) {
  return (
    <TooltipProvider delayDuration={100}>
      <UITooltip>
        <TooltipTrigger asChild>
          <button type="button" className="inline-flex text-muted-foreground hover:text-foreground ml-1 align-middle">
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs whitespace-pre-line text-xs leading-relaxed p-3">
          {text}
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  );
}

function CalcHelper({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg overflow-hidden mt-2">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-2.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors">
        <Info className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        <span>{title}</span>
        {open ? <ChevronDown className="w-3 h-3 ml-auto" /> : <ChevronRight className="w-3 h-3 ml-auto" />}
      </button>
      {open && <div className="px-3 pb-3 text-xs text-muted-foreground leading-relaxed whitespace-pre-line border-t border-border bg-muted/10 pt-2">{children}</div>}
    </div>
  );
}

function MetricCard({ label, value, sub, help, color, testId }) {
  return (
    <Card className="border shadow-sm">
      <CardContent className="p-3">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
          {label}{help && <HelpTip text={help} />}
        </p>
        <p className={cn("text-lg font-bold font-mono mt-0.5", color)} data-testid={testId}>{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function LabelVal({ label, children }) {
  return <div><label className="text-xs text-muted-foreground block mb-1">{label}</label>{children}</div>;
}

function StatusBadge({ status }) {
  const colors = {
    issued: 'bg-emerald-100 text-emerald-700', active: 'bg-emerald-100 text-emerald-700',
    funded: 'bg-blue-100 text-blue-700', executed: 'bg-blue-100 text-blue-700',
    draft: 'bg-slate-100 text-slate-600', indicative: 'bg-amber-100 text-amber-700',
    approved: 'bg-sky-100 text-sky-700', converted: 'bg-purple-100 text-purple-700',
    exercised: 'bg-indigo-100 text-indigo-700', cancelled: 'bg-red-100 text-red-700',
    rescinded: 'bg-red-100 text-red-700', returned: 'bg-orange-100 text-orange-700',
    expired: 'bg-gray-100 text-gray-600', closed: 'bg-emerald-100 text-emerald-700',
    in_progress: 'bg-blue-100 text-blue-700', granted: 'bg-emerald-100 text-emerald-700',
    vested: 'bg-indigo-100 text-indigo-700', lapsed: 'bg-gray-100 text-gray-600',
    target: 'bg-slate-100 text-slate-600', in_discussion: 'bg-amber-100 text-amber-700',
    soft_circled: 'bg-sky-100 text-sky-700', committed: 'bg-blue-100 text-blue-700',
    docs_signed: 'bg-indigo-100 text-indigo-700', withdrawn: 'bg-red-100 text-red-700',
  };
  return <Badge className={cn("text-[9px] capitalize", colors[status] || 'bg-slate-100 text-slate-700')}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

// ==================== MAIN COMPONENT ====================
export default function CapTableTab({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [holders, setHolders] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [esopPool, setEsopPool] = useState({});
  const [grants, setGrants] = useState([]);
  const [events, setEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Modals
  const [holderModal, setHolderModal] = useState(false);
  const [holderForm, setHolderForm] = useState({});
  const [editingHolder, setEditingHolder] = useState(null);
  const [instrModal, setInstrModal] = useState(false);
  const [instrForm, setInstrForm] = useState({});
  const [editingInstr, setEditingInstr] = useState(null);
  const [roundModal, setRoundModal] = useState(false);
  const [roundForm, setRoundForm] = useState({});
  const [editingRound, setEditingRound] = useState(null);
  const [grantModal, setGrantModal] = useState(false);
  const [grantForm, setGrantForm] = useState({});
  const [editingGrant, setEditingGrant] = useState(null);
  const [commitModal, setCommitModal] = useState(false);
  const [commitForm, setCommitForm] = useState({});
  const [editingCommit, setEditingCommit] = useState(null);
  const [roundDetail, setRoundDetail] = useState(null);
  const [selectedRoundId, setSelectedRoundId] = useState(null);
  const [asOfDate, setAsOfDate] = useState('');
  const [asOfOwnership, setAsOfOwnership] = useState(null);
  const [loadingAsOf, setLoadingAsOf] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [ovRes, hRes, iRes, rRes, eRes, gRes, evRes] = await Promise.all([
        fetch(`${API}/${workspaceId}`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/holders`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/instruments`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/rounds`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/esop`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/grants`, { credentials: 'include' }),
        fetch(`${API}/${workspaceId}/events?limit=100`, { credentials: 'include' }),
      ]);
      if (ovRes.ok) setOverview(await ovRes.json());
      if (hRes.ok) setHolders(await hRes.json());
      if (iRes.ok) setInstruments(await iRes.json());
      if (rRes.ok) setRounds(await rRes.json());
      if (eRes.ok) setEsopPool(await eRes.json());
      if (gRes.ok) setGrants(await gRes.json());
      if (evRes.ok) setEvents(await evRes.json());
    } catch (e) { console.error('Cap table fetch error:', e); }
    finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const holderMap = useMemo(() => {
    const m = {};
    holders.forEach(h => { m[h.holder_id] = h; });
    return m;
  }, [holders]);

  const holderName = (hid) => {
    const h = holderMap[hid];
    return h ? (h.display_name || h.legal_name || hid) : hid;
  };

  // ==================== CRUD HELPERS ====================
  const apiCall = async (url, method = 'GET', body = null) => {
    const opts = { method, credentials: 'include', headers: {} };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const t = await r.text(); throw new Error(t); }
    return r.json();
  };

  // Holders
  const saveHolder = async () => {
    try {
      if (editingHolder) {
        await apiCall(`${API}/${workspaceId}/holders/${editingHolder.holder_id}`, 'PUT', holderForm);
        toast.success('Holder updated');
      } else {
        await apiCall(`${API}/${workspaceId}/holders`, 'POST', holderForm);
        toast.success('Holder created');
      }
      setHolderModal(false); setHolderForm({}); setEditingHolder(null); fetchAll();
    } catch (e) { toast.error(e.message); }
  };
  const deleteHolder = async (id) => {
    if (!window.confirm('Delete this holder? This will not delete their instruments.')) return;
    await apiCall(`${API}/${workspaceId}/holders/${id}`, 'DELETE');
    toast.success('Deleted'); fetchAll();
  };

  // Instruments
  const saveInstrument = async () => {
    try {
      if (editingInstr) {
        await apiCall(`${API}/${workspaceId}/instruments/${editingInstr.instrument_id}`, 'PUT', instrForm);
        toast.success('Instrument updated');
      } else {
        await apiCall(`${API}/${workspaceId}/instruments`, 'POST', instrForm);
        toast.success('Instrument created');
      }
      setInstrModal(false); setInstrForm({}); setEditingInstr(null); fetchAll();
    } catch (e) { toast.error(e.message); }
  };
  const deleteInstrument = async (id) => {
    if (!window.confirm('Delete this instrument?')) return;
    await apiCall(`${API}/${workspaceId}/instruments/${id}`, 'DELETE');
    toast.success('Deleted'); fetchAll();
  };

  // Rounds
  const saveRound = async () => {
    try {
      if (editingRound) {
        await apiCall(`${API}/${workspaceId}/rounds/${editingRound.round_id}`, 'PUT', roundForm);
        toast.success('Round updated');
      } else {
        await apiCall(`${API}/${workspaceId}/rounds`, 'POST', roundForm);
        toast.success('Round created');
      }
      setRoundModal(false); setRoundForm({}); setEditingRound(null); fetchAll();
    } catch (e) { toast.error(e.message); }
  };
  const deleteRound = async (id) => {
    if (!window.confirm('Delete this round?')) return;
    await apiCall(`${API}/${workspaceId}/rounds/${id}`, 'DELETE');
    toast.success('Deleted'); fetchAll();
  };

  // Grants
  const saveGrant = async () => {
    try {
      if (editingGrant) {
        await apiCall(`${API}/${workspaceId}/grants/${editingGrant.grant_id}`, 'PUT', grantForm);
        toast.success('Grant updated');
      } else {
        await apiCall(`${API}/${workspaceId}/grants`, 'POST', grantForm);
        toast.success('Grant created');
      }
      setGrantModal(false); setGrantForm({}); setEditingGrant(null); fetchAll();
    } catch (e) { toast.error(e.message); }
  };
  const deleteGrant = async (id) => {
    if (!window.confirm('Delete this grant?')) return;
    await apiCall(`${API}/${workspaceId}/grants/${id}`, 'DELETE');
    toast.success('Deleted'); fetchAll();
  };

  // ESOP pool save
  const saveEsop = async (data) => {
    try {
      await apiCall(`${API}/${workspaceId}/esop`, 'PUT', data);
      toast.success('ESOP pool updated'); fetchAll();
    } catch (e) { toast.error(e.message); }
  };

  // Migrate
  const migrateData = async () => {
    try {
      const r = await apiCall(`${API}/${workspaceId}/migrate`, 'POST');
      if (r.migrated) {
        toast.success(`Migrated: ${r.holders} holders, ${r.instruments} instruments, ${r.rounds} rounds, ${r.grants} grants`);
        fetchAll();
      } else {
        toast.info(r.reason || 'Nothing to migrate');
      }
    } catch (e) { toast.error(e.message); }
  };

  // Commitments
  const saveCommitment = async () => {
    try {
      if (editingCommit) {
        await apiCall(`${API}/${workspaceId}/commitments/${editingCommit.commitment_id}`, 'PUT', commitForm);
        toast.success('Commitment updated');
      } else {
        await apiCall(`${API}/${workspaceId}/commitments`, 'POST', commitForm);
        toast.success('Commitment created');
      }
      setCommitModal(false); setCommitForm({}); setEditingCommit(null);
      if (selectedRoundId) fetchRoundDetail(selectedRoundId);
      fetchAll();
    } catch (e) { toast.error(e.message); }
  };
  const deleteCommitment = async (id) => {
    if (!window.confirm('Remove this commitment?')) return;
    await apiCall(`${API}/${workspaceId}/commitments/${id}`, 'DELETE');
    toast.success('Deleted');
    if (selectedRoundId) fetchRoundDetail(selectedRoundId);
    fetchAll();
  };

  // Round Detail
  const fetchRoundDetail = async (roundId) => {
    try {
      const r = await apiCall(`${API}/${workspaceId}/rounds/${roundId}/detail`);
      setRoundDetail(r);
      setSelectedRoundId(roundId);
    } catch (e) { toast.error(e.message); }
  };

  // As-of-date ownership
  const fetchAsOfOwnership = async (date) => {
    if (!date) { setAsOfOwnership(null); return; }
    setLoadingAsOf(true);
    try {
      const r = await apiCall(`${API}/${workspaceId}/ownership-as-of?as_of=${date}`);
      setAsOfOwnership(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingAsOf(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  const ow = overview?.ownership;
  // Use as-of-date data when available, otherwise current
  const activeOwnership = (asOfDate && asOfOwnership) ? asOfOwnership : ow;
  const issuedTotal = activeOwnership?.issued_outstanding?.total_shares || 0;
  const fdTotal = activeOwnership?.fully_diluted?.total_shares || 0;

  return (
    <div className="space-y-4" data-testid="cap-table-tab">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 border-b border-border pb-1">
          {[
            { key: 'overview', label: 'Overview', icon: PieChart },
            { key: 'cap-table', label: 'Cap Table', icon: Layers },
            { key: 'fully-diluted', label: 'Fully Diluted', icon: Target },
            { key: 'rounds', label: 'Rounds', icon: DollarSign },
            { key: 'esop', label: 'ESOP', icon: Award },
            { key: 'holders', label: 'Holders', icon: Users },
            { key: 'instruments', label: 'Instruments', icon: FileText },
            { key: 'history', label: 'History', icon: Clock },
          ].map(t => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`ct-tab-${t.key}`}
              className={cn("flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md font-medium transition-colors border-0",
                "data-[state=active]:bg-card data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-border"
              )}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ==================== OVERVIEW ==================== */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold">Cap Table Overview</h3>
            {instruments.length === 0 && (
              <Button size="sm" variant="outline" onClick={migrateData} data-testid="migrate-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" />Migrate Legacy Data
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Issued Shares" value={fmtN(issuedTotal)} testId="overview-issued"
              help="Total actually-issued equity shares (common, preferred, founder, advisory). Excludes options, SAFEs, and unexercised instruments." />
            <MetricCard label="Fully Diluted" value={fmtN(fdTotal)} testId="overview-fd"
              help="All issued shares + outstanding options + warrants + indicative SAFE/note conversions + unallocated ESOP pool." />
            <MetricCard label="ESOP Pool" value={fmtN(overview?.esop_authorized || 0)}
              sub={`${fmtN(overview?.esop_unallocated || 0)} available`}
              help="Total authorized option pool size. 'Available' means shares not yet granted to individuals." />
            <MetricCard label="Active SAFEs" value={String(overview?.active_safes_count || 0)}
              sub={overview?.active_safes_amount ? fmtM(overview.active_safes_amount) : undefined} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Total Invested" value={fmtM(overview?.total_invested || 0)} />
            <MetricCard label="Rounds" value={`${overview?.rounds_closed || 0} closed`} sub={`of ${overview?.rounds_count || 0} total`} />
            <MetricCard label="Holders" value={String(overview?.holder_count || 0)} />
            <MetricCard label="Instruments" value={String(overview?.instrument_count || 0)} />
          </div>

          {/* Ownership breakdown */}
          {ow?.issued_outstanding?.rows?.length > 0 && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Ownership Breakdown (Issued Outstanding)</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ow.issued_outstanding.rows.map((r, i) => (
                    <div key={r.holder_id || i} className="flex items-center gap-3">
                      <span className="text-xs w-32 truncate font-medium">{r.name}</span>
                      <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                        <div className={cn("h-full rounded-full orka-transition-colors", HOLDER_TYPE_COLORS[r.holder_type] || 'bg-slate-400')}
                          style={{ width: `${Math.min(r.ownership_pct, 100)}%` }} />
                      </div>
                      <span className="text-xs font-mono w-16 text-right">{fmtPct(r.ownership_pct)}</span>
                      <span className="text-xs font-mono w-20 text-right text-muted-foreground">{fmtN(r.shares)}</span>
                    </div>
                  ))}
                </div>
                <CalcHelper title="How is Issued Outstanding % calculated?">
                  {ow.issued_outstanding.explanation}
                </CalcHelper>
              </CardContent>
            </Card>
          )}

          {overview?.current_round && (
            <Card className="border shadow-sm border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  Active Round: {overview.current_round.round_name}
                  <StatusBadge status={overview.current_round.status} />
                  <span className="ml-auto font-mono">{fmtM(overview.current_round.target_raise)} target</span>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== CAP TABLE (Issued Outstanding) ==================== */}
        <TabsContent value="cap-table" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Current Cap Table (Issued Outstanding)</h3>
              <p className="text-xs text-muted-foreground">Legal ownership based on actually issued shares only.</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">As-of date:</label>
              <Input type="date" className="h-7 text-xs w-36" value={asOfDate} data-testid="as-of-date-input"
                onChange={e => { setAsOfDate(e.target.value); fetchAsOfOwnership(e.target.value); }} />
              {asOfDate && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setAsOfDate(''); setAsOfOwnership(null); }}>Clear</Button>}
            </div>
          </div>
          {asOfDate && asOfOwnership && (
            <CalcHelper title={`Viewing ownership as of ${fmtDate(asOfDate)}`} defaultOpen={true}>
              {`This shows ownership based only on instruments with an issue date on or before ${asOfDate}.\nInstruments created after this date are excluded from the calculation.`}
            </CalcHelper>
          )}
          {loadingAsOf && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" />Computing historical ownership...</div>}
          <CalcHelper title="What does 'Issued Outstanding' include?" defaultOpen={false}>
            {ow?.issued_outstanding?.explanation || "Includes common, preferred, founder, and advisory shares that are issued/active/funded. Excludes SAFEs, notes, options, warrants, and the ESOP pool."}
          </CalcHelper>
          {(!activeOwnership?.issued_outstanding?.rows?.length) ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">
              {asOfDate ? `No issued shares as of ${fmtDate(asOfDate)}.` : 'No issued shares yet. Add holders and instruments to build your cap table.'}
            </CardContent></Card>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Holder</th>
                    <th className="p-2 text-left font-medium">Type</th>
                    <th className="p-2 text-right font-medium">Shares</th>
                    <th className="p-2 text-right font-medium">Ownership %</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOwnership.issued_outstanding.rows.map((r, i) => (
                    <tr key={r.holder_id} className="border-t border-border hover:bg-muted/20" data-testid={`ct-issued-row-${i}`}>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{(r.holder_type || '').replace(/_/g, ' ')}</Badge></td>
                      <td className="p-2 text-right font-mono">{fmtN(r.shares)}</td>
                      <td className="p-2 text-right font-mono font-semibold">{fmtPct(r.ownership_pct)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                    <td className="p-2" colSpan={2}>Total</td>
                    <td className="p-2 text-right font-mono">{fmtN(issuedTotal)}</td>
                    <td className="p-2 text-right font-mono">100.00%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ==================== FULLY DILUTED ==================== */}
        <TabsContent value="fully-diluted" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Fully Diluted Cap Table</h3>
              <p className="text-xs text-muted-foreground">Includes all issued shares, options, warrants, SAFE/note conversions, and ESOP pool.</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">As-of date:</label>
              <Input type="date" className="h-7 text-xs w-36" value={asOfDate} data-testid="fd-as-of-date-input"
                onChange={e => { setAsOfDate(e.target.value); fetchAsOfOwnership(e.target.value); }} />
              {asOfDate && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setAsOfDate(''); setAsOfOwnership(null); }}>Clear</Button>}
            </div>
          </div>
          {loadingAsOf && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" />Computing...</div>}
          <CalcHelper title="How is Fully Diluted calculated?" defaultOpen={false}>
            {ow?.fully_diluted?.explanation || "Fully diluted = issued shares + options + warrants + indicative SAFE/note conversions + unallocated ESOP pool."}
          </CalcHelper>
          {(!activeOwnership?.fully_diluted?.rows?.length && !activeOwnership?.fully_diluted?.esop_unallocated) ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">
              No data yet. Add holders, instruments, and an ESOP pool.
            </CardContent></Card>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Holder</th>
                    <th className="p-2 text-left font-medium">Type</th>
                    <th className="p-2 text-right font-medium">Issued</th>
                    <th className="p-2 text-right font-medium">Options</th>
                    <th className="p-2 text-right font-medium">SAFEs</th>
                    <th className="p-2 text-right font-medium">Notes</th>
                    <th className="p-2 text-right font-medium">Warrants</th>
                    <th className="p-2 text-right font-medium">Total</th>
                    <th className="p-2 text-right font-medium">FD %</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeOwnership?.fully_diluted?.rows || []).map((r, i) => (
                    <tr key={r.holder_id} className="border-t border-border hover:bg-muted/20" data-testid={`ct-fd-row-${i}`}>
                      <td className="p-2 font-medium">{r.name}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{(r.holder_type || '').replace(/_/g, ' ')}</Badge></td>
                      <td className="p-2 text-right font-mono">{r.issued_shares ? fmtN(r.issued_shares) : '-'}</td>
                      <td className="p-2 text-right font-mono text-violet-600">{r.option_shares ? fmtN(r.option_shares) : '-'}</td>
                      <td className="p-2 text-right font-mono text-amber-600">{r.safe_shares ? fmtN(r.safe_shares) : '-'}</td>
                      <td className="p-2 text-right font-mono text-orange-600">{r.note_shares ? fmtN(r.note_shares) : '-'}</td>
                      <td className="p-2 text-right font-mono text-rose-600">{r.warrant_shares ? fmtN(r.warrant_shares) : '-'}</td>
                      <td className="p-2 text-right font-mono font-medium">{fmtN(r.total_shares)}</td>
                      <td className="p-2 text-right font-mono font-semibold">{fmtPct(r.ownership_pct)}</td>
                    </tr>
                  ))}
                  {(activeOwnership?.fully_diluted?.esop_unallocated > 0) && (
                    <tr className="border-t border-border bg-violet-50/30 dark:bg-violet-900/10">
                      <td className="p-2 font-medium">ESOP Pool (Unallocated)</td>
                      <td className="p-2"><Badge variant="outline" className="text-[9px]">Reserve</Badge></td>
                      <td className="p-2" colSpan={5}></td>
                      <td className="p-2 text-right font-mono font-medium">{fmtN(activeOwnership.fully_diluted.esop_unallocated)}</td>
                      <td className="p-2 text-right font-mono font-semibold">{fmtPct(activeOwnership.fully_diluted.esop_pct)}</td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                    <td className="p-2" colSpan={7}>Total Fully Diluted</td>
                    <td className="p-2 text-right font-mono">{fmtN(fdTotal)}</td>
                    <td className="p-2 text-right font-mono">100.00%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* SAFE Conversion details */}
          {ow?.fully_diluted?.safe_conversions?.length > 0 && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">SAFE Conversion Details (Indicative)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {ow.fully_diluted.safe_conversions.map((c, i) => (
                  <div key={c.instrument_id || i} className="p-3 bg-amber-50/50 dark:bg-amber-950/10 rounded-lg border border-amber-200/50 dark:border-amber-800/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{c.name}</span>
                      <span className="text-xs font-mono">{fmtN(c.shares)} shares @ ${(c.conversion_price || 0).toFixed(4)}/sh</span>
                    </div>
                    <CalcHelper title="Show conversion calculation">
                      {c.explanation}
                    </CalcHelper>
                  </div>
                ))}
                {ow.conversion_round?.round_name && (
                  <p className="text-[10px] text-muted-foreground">
                    Conversions calculated using round: <strong>{ow.conversion_round.round_name}</strong> (share price: ${(ow.conversion_round.share_price || 0).toFixed(4)})
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {ow?.fully_diluted?.note_conversions?.length > 0 && (
            <Card className="border shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Convertible Note Conversion Details (Indicative)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {ow.fully_diluted.note_conversions.map((c, i) => (
                  <div key={c.instrument_id || i} className="p-3 bg-orange-50/50 dark:bg-orange-950/10 rounded-lg border border-orange-200/50 dark:border-orange-800/30">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{c.name}</span>
                      <span className="text-xs font-mono">{fmtN(c.shares)} shares @ ${(c.conversion_price || 0).toFixed(4)}/sh</span>
                    </div>
                    <CalcHelper title="Show conversion calculation">{c.explanation}</CalcHelper>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== ROUNDS (Workspace) ==================== */}
        <TabsContent value="rounds" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Funding Rounds</h3>
              <p className="text-xs text-muted-foreground">Track fundraising rounds with investor commitment pipeline.</p>
            </div>
            <div className="flex gap-2">
              {selectedRoundId && <Button size="sm" variant="ghost" onClick={() => { setSelectedRoundId(null); setRoundDetail(null); }} data-testid="back-to-rounds-btn"><ChevronRight className="w-3.5 h-3.5 mr-1 rotate-180" />All Rounds</Button>}
              <Button size="sm" onClick={() => { setEditingRound(null); setRoundForm({}); setRoundModal(true); }} data-testid="add-round-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />Add Round
              </Button>
            </div>
          </div>
          <CalcHelper title="How the Round Workspace works">
            {`Each round tracks its investor commitment pipeline through stages:\n\nTarget > In Discussion > Soft Circled > Committed > Docs Signed > Funded > Issued\n\nThis lets you model ownership before the round is final. The system shows:\n- Target vs Committed vs Funded progress\n- Individual investor status\n- Expected ownership impact`}
          </CalcHelper>

          {/* Round detail view (with commitments) */}
          {selectedRoundId && roundDetail ? (
            <div className="space-y-4">
              <Card className="border shadow-sm">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold">{roundDetail.round_name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={roundDetail.status} />
                        <Badge variant="outline" className="text-[9px] capitalize">{(roundDetail.round_type || '').replace(/_/g, ' ')}</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Target Raise</p>
                      <p className="text-lg font-bold font-mono">{fmtM(roundDetail.target_raise)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <MetricCard label="Pre-Money" value={fmtM(roundDetail.pre_money_valuation)} />
                    <MetricCard label="Post-Money" value={fmtM(roundDetail.post_money_valuation)} />
                    <MetricCard label="Share Price" value={roundDetail.assumed_share_price ? `$${Number(roundDetail.assumed_share_price).toFixed(4)}` : '-'} />
                    <MetricCard label="Target ESOP" value={roundDetail.target_esop_pool_post_round ? `${roundDetail.target_esop_pool_post_round}%` : '-'} />
                  </div>

                  {/* Progress bar */}
                  {roundDetail.stats && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Fundraising Progress</span>
                        <span className="font-mono font-medium">{fmtM(roundDetail.stats.total_committed)} committed / {fmtM(roundDetail.stats.total_funded)} funded</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden flex">
                        <div className="h-full bg-emerald-500 orka-transition-colors" style={{ width: `${Math.min(roundDetail.stats.pct_funded, 100)}%` }} />
                        <div className="h-full bg-blue-400 orka-transition-colors" style={{ width: `${Math.max(Math.min(roundDetail.stats.pct_of_target - roundDetail.stats.pct_funded, 100 - roundDetail.stats.pct_funded), 0)}%` }} />
                      </div>
                      <div className="flex gap-4 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full inline-block" />Funded ({roundDetail.stats.pct_funded}%)</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-400 rounded-full inline-block" />Committed ({roundDetail.stats.pct_of_target}%)</span>
                        <span className="ml-auto">{roundDetail.stats.commitment_count} investor(s)</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Commitments table */}
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Investor Commitments</h4>
                <Button size="sm" onClick={() => { setEditingCommit(null); setCommitForm({ round_id: selectedRoundId }); setCommitModal(true); }} data-testid="add-commitment-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Commitment
                </Button>
              </div>
              {(!roundDetail.commitments?.length) ? (
                <Card className="border"><CardContent className="p-6 text-center text-muted-foreground text-sm">No commitments yet. Add investors to this round.</CardContent></Card>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left font-medium">Investor</th>
                        <th className="p-2 text-left font-medium">Type</th>
                        <th className="p-2 text-left font-medium">Status</th>
                        <th className="p-2 text-right font-medium">Target</th>
                        <th className="p-2 text-right font-medium">Committed</th>
                        <th className="p-2 text-right font-medium">Funded</th>
                        <th className="p-2 text-left font-medium">Expected Close</th>
                        <th className="p-2 text-right font-medium w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roundDetail.commitments.map((c, i) => (
                        <tr key={c.commitment_id} className="border-t border-border hover:bg-muted/20" data-testid={`commit-row-${i}`}>
                          <td className="p-2 font-medium">{c.holder_name || '-'}</td>
                          <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{(c.holder_type || '').replace(/_/g, ' ')}</Badge></td>
                          <td className="p-2"><StatusBadge status={c.status} /></td>
                          <td className="p-2 text-right font-mono">{fmtM(c.target_amount)}</td>
                          <td className="p-2 text-right font-mono">{fmtM(c.committed_amount)}</td>
                          <td className="p-2 text-right font-mono text-emerald-600">{fmtM(c.funded_amount)}</td>
                          <td className="p-2">{c.expected_close_date ? fmtDate(c.expected_close_date) : '-'}</td>
                          <td className="p-2 text-right">
                            <div className="flex gap-0.5 justify-end">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingCommit(c); setCommitForm(c); setCommitModal(true); }}><Settings2 className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteCommitment(c.commitment_id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            /* Round list view */
            <>
              {rounds.length === 0 ? (
                <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">No rounds yet.</CardContent></Card>
              ) : (
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2 text-left font-medium">Round</th>
                        <th className="p-2 text-left font-medium">Type</th>
                        <th className="p-2 text-right font-medium">Pre-Money</th>
                        <th className="p-2 text-right font-medium">Target Raise</th>
                        <th className="p-2 text-right font-medium">Post-Money</th>
                        <th className="p-2 text-right font-medium">Share Price</th>
                        <th className="p-2 text-left font-medium">Status</th>
                        <th className="p-2 text-right font-medium w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rounds.map((rd, i) => (
                        <tr key={rd.round_id} className="border-t border-border hover:bg-muted/20 cursor-pointer" data-testid={`round-row-${i}`}
                          onClick={() => fetchRoundDetail(rd.round_id)}>
                          <td className="p-2 font-medium text-blue-600 hover:underline">{rd.round_name}</td>
                          <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{(rd.round_type || '').replace(/_/g, ' ')}</Badge></td>
                          <td className="p-2 text-right font-mono">{fmtM(rd.pre_money_valuation)}</td>
                          <td className="p-2 text-right font-mono">{fmtM(rd.target_raise)}</td>
                          <td className="p-2 text-right font-mono">{fmtM(rd.post_money_valuation)}</td>
                          <td className="p-2 text-right font-mono">{rd.assumed_share_price ? `$${Number(rd.assumed_share_price).toFixed(4)}` : '-'}</td>
                          <td className="p-2"><StatusBadge status={rd.status} /></td>
                          <td className="p-2 text-right" onClick={e => e.stopPropagation()}>
                            <div className="flex gap-0.5 justify-end">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingRound(rd); setRoundForm(rd); setRoundModal(true); }}><Settings2 className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteRound(rd.round_id)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ==================== ESOP ==================== */}
        <TabsContent value="esop" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">ESOP (Employee Stock Option Pool)</h3>
              <p className="text-xs text-muted-foreground">Manage the option pool and individual grants.</p>
            </div>
            <Button size="sm" onClick={() => { setEditingGrant(null); setGrantForm({ cliff_months: 12, vesting_months: 48 }); setGrantModal(true); }} data-testid="add-grant-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Grant
            </Button>
          </div>
          <CalcHelper title="Understanding the ESOP pool">
            {`The ESOP (Employee Stock Option Pool) reserves a set number of shares for employee compensation.\n\nAuthorized: Total shares allocated to the pool by the board.\nGranted: Shares allocated to individual employees/advisors.\nUnallocated: Authorized - Granted = available for future grants.\n\nInvestors often require the pool to be sized (e.g. 10-20%) before their round, which dilutes founders.`}
          </CalcHelper>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricCard label="Authorized Pool" value={fmtN(esopPool.authorized_quantity || 0)} sub="Total shares reserved" />
            <MetricCard label="Granted" value={fmtN(grants.reduce((s, g) => s + (g.grant_status !== 'cancelled' && g.grant_status !== 'lapsed' ? (g.quantity || 0) : 0), 0))}
              sub={`${grants.filter(g => g.grant_status !== 'cancelled' && g.grant_status !== 'lapsed').length} active grants`} />
            <MetricCard label="Unallocated" value={fmtN(overview?.esop_unallocated || 0)}
              color={(overview?.esop_unallocated || 0) < 0 ? 'text-red-500' : 'text-emerald-600'} />
            <div className="flex flex-col gap-1">
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Set Pool Size</label>
              <div className="flex gap-2 items-center">
                <Input type="number" value={esopPool.authorized_quantity || ''} data-testid="esop-pool-input"
                  onChange={e => setEsopPool(p => ({ ...p, authorized_quantity: e.target.value }))}
                  className="h-9 text-sm w-28" placeholder="Shares" />
                <Button size="sm" variant="outline" onClick={() => saveEsop(esopPool)}>Save</Button>
              </div>
            </div>
          </div>

          <CalcHelper title="How vesting and cliff work">
            {`Standard vesting: 4-year vesting with 1-year cliff.\n\nCliff: No shares vest until the cliff period ends (typically 12 months). After the cliff, all shares that would have vested during that period vest at once.\n\nMonthly vesting: After the cliff, shares vest monthly over the remaining period.\n\nExample: 10,000 options, 4-year vest, 1-year cliff:\n- Months 0-11: 0 vested\n- Month 12 (cliff): 2,500 vest at once\n- Months 13-48: ~208 vest each month`}
          </CalcHelper>

          {grants.length === 0 ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">No grants yet.</CardContent></Card>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Recipient</th>
                    <th className="p-2 text-right font-medium">Quantity</th>
                    <th className="p-2 text-right font-medium">Strike Price</th>
                    <th className="p-2 text-left font-medium">Grant Date</th>
                    <th className="p-2 text-left font-medium">Vesting</th>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-right font-medium w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map((g, i) => (
                    <tr key={g.grant_id} className="border-t border-border hover:bg-muted/20" data-testid={`grant-row-${i}`}>
                      <td className="p-2 font-medium">{g.recipient_name || holderName(g.holder_id) || '-'}</td>
                      <td className="p-2 text-right font-mono">{fmtN(g.quantity)}</td>
                      <td className="p-2 text-right font-mono">{g.strike_price ? `$${Number(g.strike_price).toFixed(4)}` : '-'}</td>
                      <td className="p-2">{g.grant_date ? fmtDate(g.grant_date) : '-'}</td>
                      <td className="p-2 text-muted-foreground">{g.vesting_months ? `${g.vesting_months}mo / ${g.cliff_months || 12}mo cliff` : '-'}</td>
                      <td className="p-2"><StatusBadge status={g.grant_status} /></td>
                      <td className="p-2 text-right">
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingGrant(g); setGrantForm(g); setGrantModal(true); }}><Settings2 className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteGrant(g.grant_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ==================== HOLDERS ==================== */}
        <TabsContent value="holders" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Holders</h3>
              <p className="text-xs text-muted-foreground">All equity holders, investors, and potential owners.</p>
            </div>
            <Button size="sm" onClick={() => { setEditingHolder(null); setHolderForm({}); setHolderModal(true); }} data-testid="add-holder-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Holder
            </Button>
          </div>
          {holders.length === 0 ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">No holders yet. Add your first holder to get started.</CardContent></Card>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Name</th>
                    <th className="p-2 text-left font-medium">Type</th>
                    <th className="p-2 text-left font-medium">Email</th>
                    <th className="p-2 text-right font-medium">Instruments</th>
                    <th className="p-2 text-left font-medium">Notes</th>
                    <th className="p-2 text-right font-medium w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {holders.map((h, i) => {
                    const instrCount = instruments.filter(ins => ins.holder_id === h.holder_id).length;
                    return (
                      <tr key={h.holder_id} className="border-t border-border hover:bg-muted/20" data-testid={`holder-row-${i}`}>
                        <td className="p-2 font-medium">{h.display_name || h.legal_name}</td>
                        <td className="p-2"><Badge variant="outline" className="text-[9px] capitalize">{(h.holder_type || '').replace(/_/g, ' ')}</Badge></td>
                        <td className="p-2 text-muted-foreground">{h.email || '-'}</td>
                        <td className="p-2 text-right font-mono">{instrCount}</td>
                        <td className="p-2 text-muted-foreground max-w-[200px] truncate">{h.notes || '-'}</td>
                        <td className="p-2 text-right">
                          <div className="flex gap-0.5 justify-end">
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingHolder(h); setHolderForm(h); setHolderModal(true); }}><Settings2 className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteHolder(h.holder_id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ==================== INSTRUMENTS ==================== */}
        <TabsContent value="instruments" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Instruments</h3>
              <p className="text-xs text-muted-foreground">All securities: shares, SAFEs, notes, options, warrants.</p>
            </div>
            <Button size="sm" onClick={() => { setEditingInstr(null); setInstrForm({}); setInstrModal(true); }} data-testid="add-instrument-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Instrument
            </Button>
          </div>
          <CalcHelper title="Understanding instrument types">
            {`Common Shares: Standard equity, typically held by founders and employees.\nPreferred Shares: Investor shares, often with liquidation preference.\nFounder Shares: Shares issued to founders at incorporation.\nAdvisory Shares: Equity granted to advisors, often with vesting.\nOptions (ESOP): Right to purchase shares at a strike price.\nRSU: Restricted Stock Units - shares granted after vesting.\nSAFE: Simple Agreement for Future Equity - converts at next priced round.\nConvertible Note: Debt that converts to equity with interest.\nWarrant: Right to buy shares at a set price before expiry.\nPending Subscription: Committed but not yet issued shares.`}
          </CalcHelper>
          {instruments.length === 0 ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">No instruments yet.</CardContent></Card>
          ) : (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="p-2 text-left font-medium">Holder</th>
                    <th className="p-2 text-left font-medium">Type</th>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-right font-medium">Qty / Shares</th>
                    <th className="p-2 text-right font-medium">Invested</th>
                    <th className="p-2 text-left font-medium">Issue Date</th>
                    <th className="p-2 text-left font-medium">Details</th>
                    <th className="p-2 text-right font-medium w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instruments.map((ins, i) => (
                    <tr key={ins.instrument_id} className="border-t border-border hover:bg-muted/20" data-testid={`instr-row-${i}`}>
                      <td className="p-2 font-medium">{holderName(ins.holder_id)}</td>
                      <td className="p-2"><Badge className={cn("text-[9px]", INST_TYPE_COLORS[ins.instrument_type] || '')}>{INSTRUMENT_TYPES.find(t => t.value === ins.instrument_type)?.label || ins.instrument_type}</Badge></td>
                      <td className="p-2"><StatusBadge status={ins.status} /></td>
                      <td className="p-2 text-right font-mono">{fmtN(ins.quantity)}</td>
                      <td className="p-2 text-right font-mono">{ins.amount_invested ? fmtM(ins.amount_invested) : '-'}</td>
                      <td className="p-2">{ins.issue_date ? fmtDate(ins.issue_date) : '-'}</td>
                      <td className="p-2 text-muted-foreground text-[10px]">
                        {ins.instrument_type === 'safe' && (<span>{ins.safe_type || 'post_money'} {ins.valuation_cap ? `Cap: ${fmtM(ins.valuation_cap)}` : ''} {ins.discount_rate ? `${ins.discount_rate}% disc` : ''}</span>)}
                        {ins.instrument_type === 'convertible_note' && (<span>Cap: {fmtM(ins.valuation_cap)} {ins.interest_rate ? `${ins.interest_rate}% int` : ''}</span>)}
                        {ins.instrument_type === 'option' && (<span>Strike: ${Number(ins.strike_price || 0).toFixed(4)}</span>)}
                        {ins.instrument_type === 'warrant' && (<span>Strike: ${Number(ins.exercise_price || 0).toFixed(4)} {ins.expiry_date ? `Exp: ${fmtDate(ins.expiry_date)}` : ''}</span>)}
                        {ins.class_name && <span className="ml-1">({ins.class_name})</span>}
                      </td>
                      <td className="p-2 text-right">
                        <div className="flex gap-0.5 justify-end">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditingInstr(ins); setInstrForm(ins); setInstrModal(true); }}><Settings2 className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => deleteInstrument(ins.instrument_id)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ==================== HISTORY ==================== */}
        <TabsContent value="history" className="space-y-4 mt-4">
          <div>
            <h3 className="text-sm font-semibold">Event History</h3>
            <p className="text-xs text-muted-foreground">Immutable audit trail of all cap table changes.</p>
          </div>
          {events.length === 0 ? (
            <Card className="border"><CardContent className="p-8 text-center text-muted-foreground text-sm">No events yet.</CardContent></Card>
          ) : (
            <div className="space-y-1.5">
              {events.map((ev, i) => (
                <div key={ev.event_id || i} className="flex items-start gap-3 p-2.5 rounded-lg border border-border hover:bg-muted/20 transition-colors" data-testid={`event-row-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[9px] capitalize">{(ev.event_type || '').replace(/_/g, ' ')}</Badge>
                      <span className="text-[10px] text-muted-foreground">{fmtDate(ev.created_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{ev.description}</p>
                  </div>
                  {(ev.quantity_delta !== 0 || ev.cash_amount_delta !== 0) && (
                    <div className="text-right shrink-0">
                      {ev.quantity_delta !== 0 && <p className="text-xs font-mono">{ev.quantity_delta > 0 ? '+' : ''}{fmtN(ev.quantity_delta)} shares</p>}
                      {ev.cash_amount_delta !== 0 && <p className="text-xs font-mono text-emerald-600">{fmtM(ev.cash_amount_delta)}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ==================== MODALS ==================== */}

      {/* Holder Modal */}
      <Dialog open={holderModal} onOpenChange={o => { if (!o) { setHolderModal(false); setHolderForm({}); setEditingHolder(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingHolder ? 'Edit' : 'Add'} Holder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <LabelVal label="Display Name *"><Input value={holderForm.display_name || ''} onChange={e => setHolderForm(f => ({ ...f, display_name: e.target.value }))} className="h-8 text-sm" data-testid="holder-name-input" /></LabelVal>
            <LabelVal label="Legal Name"><Input value={holderForm.legal_name || ''} onChange={e => setHolderForm(f => ({ ...f, legal_name: e.target.value }))} className="h-8 text-sm" /></LabelVal>
            <LabelVal label="Type">
              <Select value={holderForm.holder_type || 'individual_investor'} onValueChange={v => setHolderForm(f => ({ ...f, holder_type: v }))}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{HOLDER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </LabelVal>
            <LabelVal label="Email"><Input value={holderForm.email || ''} onChange={e => setHolderForm(f => ({ ...f, email: e.target.value }))} className="h-8 text-sm" /></LabelVal>
            <LabelVal label="Notes"><Input value={holderForm.notes || ''} onChange={e => setHolderForm(f => ({ ...f, notes: e.target.value }))} className="h-8 text-sm" /></LabelVal>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolderModal(false)}>Cancel</Button>
            <Button onClick={saveHolder} data-testid="save-holder-btn">{editingHolder ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instrument Modal */}
      <Dialog open={instrModal} onOpenChange={o => { if (!o) { setInstrModal(false); setInstrForm({}); setEditingInstr(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingInstr ? 'Edit' : 'Add'} Instrument</DialogTitle></DialogHeader>
          <InstrumentForm form={instrForm} onChange={setInstrForm} holders={holders} rounds={rounds} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstrModal(false)}>Cancel</Button>
            <Button onClick={saveInstrument} data-testid="save-instrument-btn">{editingInstr ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Round Modal */}
      <Dialog open={roundModal} onOpenChange={o => { if (!o) { setRoundModal(false); setRoundForm({}); setEditingRound(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingRound ? 'Edit' : 'Add'} Funding Round</DialogTitle></DialogHeader>
          <RoundFormComponent form={roundForm} onChange={setRoundForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoundModal(false)}>Cancel</Button>
            <Button onClick={saveRound} data-testid="save-round-btn">{editingRound ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Modal */}
      <Dialog open={grantModal} onOpenChange={o => { if (!o) { setGrantModal(false); setGrantForm({}); setEditingGrant(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingGrant ? 'Edit' : 'Add'} ESOP Grant</DialogTitle></DialogHeader>
          <GrantFormComponent form={grantForm} onChange={setGrantForm} holders={holders} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantModal(false)}>Cancel</Button>
            <Button onClick={saveGrant} data-testid="save-grant-btn">{editingGrant ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commitment Modal */}
      <Dialog open={commitModal} onOpenChange={o => { if (!o) { setCommitModal(false); setCommitForm({}); setEditingCommit(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingCommit ? 'Edit' : 'Add'} Commitment</DialogTitle></DialogHeader>
          <CommitmentFormComponent form={commitForm} onChange={setCommitForm} holders={holders} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitModal(false)}>Cancel</Button>
            <Button onClick={saveCommitment} data-testid="save-commitment-btn">{editingCommit ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== FORM COMPONENTS ====================

function InstrumentForm({ form, onChange, holders, rounds }) {
  const set = (k, v) => onChange(p => ({ ...p, [k]: v }));
  const isSafe = form.instrument_type === 'safe';
  const isNote = form.instrument_type === 'convertible_note';
  const isOption = form.instrument_type === 'option' || form.instrument_type === 'rsu' || form.instrument_type === 'phantom_equity';
  const isWarrant = form.instrument_type === 'warrant';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <LabelVal label="Holder *">
          <Select value={form.holder_id || '_none'} onValueChange={v => set('holder_id', v === '_none' ? '' : v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select holder" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">-- Select --</SelectItem>
              {holders.map(h => <SelectItem key={h.holder_id} value={h.holder_id}>{h.display_name || h.legal_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Instrument Type *">
          <Select value={form.instrument_type || 'common_share'} onValueChange={v => set('instrument_type', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{INSTRUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Status">
          <Select value={form.status || 'issued'} onValueChange={v => set('status', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{INSTRUMENT_STATUSES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Class Name"><Input value={form.class_name || ''} onChange={e => set('class_name', e.target.value)} className="h-8 text-sm" placeholder="e.g. Series A" /></LabelVal>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <LabelVal label="Quantity / Shares"><Input type="number" value={form.quantity || ''} onChange={e => set('quantity', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Amount Invested"><Input type="number" value={form.amount_invested || ''} onChange={e => set('amount_invested', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Issue Date"><Input type="date" value={form.issue_date || ''} onChange={e => set('issue_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Currency"><Input value={form.currency || 'USD'} onChange={e => set('currency', e.target.value)} className="h-8 text-sm" /></LabelVal>
      </div>

      {(isSafe || isNote) && (
        <div className="p-3 bg-amber-50/50 dark:bg-amber-950/10 rounded-lg border border-amber-200/50 dark:border-amber-800/30">
          <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 mb-2">{isSafe ? 'SAFE Terms' : 'Convertible Note Terms'}</p>
          <div className="grid grid-cols-2 gap-3">
            {isSafe && (
              <LabelVal label="SAFE Type">
                <Select value={form.safe_type || 'post_money'} onValueChange={v => set('safe_type', v)}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="post_money">Post-Money</SelectItem>
                    <SelectItem value="pre_money">Pre-Money</SelectItem>
                  </SelectContent>
                </Select>
              </LabelVal>
            )}
            <LabelVal label="Valuation Cap"><Input type="number" value={form.valuation_cap || ''} onChange={e => set('valuation_cap', e.target.value)} className="h-8 text-sm" /></LabelVal>
            <LabelVal label="Discount Rate %"><Input type="number" value={form.discount_rate || ''} onChange={e => set('discount_rate', e.target.value)} className="h-8 text-sm" /></LabelVal>
            {isNote && (
              <>
                <LabelVal label="Principal"><Input type="number" value={form.principal_amount || ''} onChange={e => set('principal_amount', e.target.value)} className="h-8 text-sm" /></LabelVal>
                <LabelVal label="Interest Rate %"><Input type="number" value={form.interest_rate || ''} onChange={e => set('interest_rate', e.target.value)} className="h-8 text-sm" /></LabelVal>
                <LabelVal label="Maturity Date"><Input type="date" value={form.maturity_date || ''} onChange={e => set('maturity_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
              </>
            )}
          </div>
          <CalcHelper title={isSafe ? "How SAFE conversion works" : "How note conversion works"}>
            {isSafe
              ? `Post-Money SAFE: Ownership % = Investment / Valuation Cap. This gives investors a known percentage at signing (YC standard).\n\nPre-Money SAFE: Converts at the LOWER of:\n1. Cap Price = Valuation Cap / Pre-Money Shares\n2. Discount Price = Round Price x (1 - Discount%)\n\nShares = Investment / Conversion Price`
              : `Convertible Note: A loan that converts to equity at the next priced round.\n\nTotal Amount = Principal + Accrued Interest\nConversion Price = LOWER of (Cap Price, Discounted Round Price)\nShares = Total Amount / Conversion Price`
            }
          </CalcHelper>
        </div>
      )}

      {(isOption || isWarrant) && (
        <div className="p-3 bg-violet-50/50 dark:bg-violet-950/10 rounded-lg border border-violet-200/50 dark:border-violet-800/30">
          <p className="text-[10px] font-semibold text-violet-700 dark:text-violet-300 mb-2">{isWarrant ? 'Warrant Terms' : 'Option / Vesting Terms'}</p>
          <div className="grid grid-cols-2 gap-3">
            <LabelVal label={isWarrant ? "Exercise Price" : "Strike Price"}>
              <Input type="number" step="0.0001" value={isWarrant ? (form.exercise_price || '') : (form.strike_price || '')}
                onChange={e => set(isWarrant ? 'exercise_price' : 'strike_price', e.target.value)} className="h-8 text-sm" />
            </LabelVal>
            {isWarrant && <LabelVal label="Expiry Date"><Input type="date" value={form.expiry_date || ''} onChange={e => set('expiry_date', e.target.value)} className="h-8 text-sm" /></LabelVal>}
            {isOption && (
              <>
                <LabelVal label="Vesting Start"><Input type="date" value={form.vesting_start_date || ''} onChange={e => set('vesting_start_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
                <LabelVal label="Vesting (months)"><Input type="number" value={form.vesting_months || ''} onChange={e => set('vesting_months', e.target.value)} placeholder="48" className="h-8 text-sm" /></LabelVal>
                <LabelVal label="Cliff (months)"><Input type="number" value={form.cliff_months || ''} onChange={e => set('cliff_months', e.target.value)} placeholder="12" className="h-8 text-sm" /></LabelVal>
              </>
            )}
          </div>
        </div>
      )}

      {rounds.length > 0 && (
        <LabelVal label="Linked Round">
          <Select value={form.round_id || '_none'} onValueChange={v => set('round_id', v === '_none' ? '' : v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">None</SelectItem>
              {rounds.map(r => <SelectItem key={r.round_id} value={r.round_id}>{r.round_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </LabelVal>
      )}

      <LabelVal label="Notes"><Input value={form.notes || ''} onChange={e => set('notes', e.target.value)} className="h-8 text-sm" placeholder="Optional notes" /></LabelVal>
    </div>
  );
}

function RoundFormComponent({ form, onChange }) {
  const set = (k, v) => onChange(p => ({ ...p, [k]: v }));
  const pre = parseFloat(form.pre_money_valuation) || 0;
  const raise = parseFloat(form.target_raise) || 0;
  const post = pre + raise;

  return (
    <div className="space-y-3">
      <CalcHelper title="Round calculation formulas">
        {`Pre-Money Valuation: Company value before new investment.\nPost-Money = Pre-Money + Amount Raised\nShare Price = Pre-Money / Pre-Money Shares\nNew Shares = Amount Raised / Share Price`}
      </CalcHelper>
      <div className="grid grid-cols-2 gap-3">
        <LabelVal label="Round Name *"><Input value={form.round_name || ''} onChange={e => set('round_name', e.target.value)} placeholder="e.g. Seed" className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Type">
          <Select value={form.round_type || 'priced_equity'} onValueChange={v => set('round_type', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{ROUND_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Pre-Money Valuation"><Input type="number" value={form.pre_money_valuation || ''} onChange={e => set('pre_money_valuation', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Target Raise"><Input type="number" value={form.target_raise || ''} onChange={e => set('target_raise', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Post-Money (auto)">
          <div className="h-8 px-3 flex items-center text-sm font-mono bg-muted/50 rounded-md border">{post > 0 ? fmtM(post) : '-'}</div>
        </LabelVal>
        <LabelVal label="Assumed Share Price"><Input type="number" step="0.0001" value={form.assumed_share_price || ''} onChange={e => set('assumed_share_price', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Minimum Raise"><Input type="number" value={form.minimum_raise || ''} onChange={e => set('minimum_raise', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Stretch Raise"><Input type="number" value={form.stretch_raise || ''} onChange={e => set('stretch_raise', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Target ESOP Post-Round %"><Input type="number" value={form.target_esop_pool_post_round || ''} onChange={e => set('target_esop_pool_post_round', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Currency"><Input value={form.currency || 'USD'} onChange={e => set('currency', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Start Date"><Input type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Close Date"><Input type="date" value={form.close_date || ''} onChange={e => set('close_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Status">
          <Select value={form.status || 'indicative'} onValueChange={v => set('status', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{ROUND_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
      </div>
      <LabelVal label="Notes"><Input value={form.notes || ''} onChange={e => set('notes', e.target.value)} className="h-8 text-sm" /></LabelVal>
    </div>
  );
}

function GrantFormComponent({ form, onChange, holders }) {
  const set = (k, v) => onChange(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-3">
      <CalcHelper title="How vesting and cliff work">
        {`Standard: 4-year vest, 1-year cliff.\nMonths 0-11: 0 vested. Month 12: 25% vests. Months 13-48: ~2% per month.\n\nStrike Price: The price at which the holder can purchase shares, usually Fair Market Value on grant date.`}
      </CalcHelper>
      <div className="grid grid-cols-2 gap-3">
        <LabelVal label="Recipient Name *"><Input value={form.recipient_name || ''} onChange={e => set('recipient_name', e.target.value)} className="h-8 text-sm" /></LabelVal>
        {holders.length > 0 && (
          <LabelVal label="Link to Holder">
            <Select value={form.holder_id || '_none'} onValueChange={v => set('holder_id', v === '_none' ? '' : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {holders.map(h => <SelectItem key={h.holder_id} value={h.holder_id}>{h.display_name || h.legal_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </LabelVal>
        )}
        <LabelVal label="Quantity (shares)"><Input type="number" value={form.quantity || ''} onChange={e => set('quantity', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Strike Price"><Input type="number" step="0.0001" value={form.strike_price || ''} onChange={e => set('strike_price', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Grant Date"><Input type="date" value={form.grant_date || ''} onChange={e => set('grant_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Vesting Start"><Input type="date" value={form.vesting_start_date || ''} onChange={e => set('vesting_start_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Vesting (months)"><Input type="number" value={form.vesting_months || ''} onChange={e => set('vesting_months', e.target.value)} placeholder="48" className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Cliff (months)"><Input type="number" value={form.cliff_months || ''} onChange={e => set('cliff_months', e.target.value)} placeholder="12" className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Status">
          <Select value={form.grant_status || 'indicative'} onValueChange={v => set('grant_status', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{GRANT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
      </div>
      <LabelVal label="Notes"><Input value={form.notes || ''} onChange={e => set('notes', e.target.value)} className="h-8 text-sm" /></LabelVal>
    </div>
  );
}


function CommitmentFormComponent({ form, onChange, holders }) {
  const set = (k, v) => onChange(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-3">
      <CalcHelper title="Commitment pipeline stages">
        {`Target: Initial allocation size being discussed.\nIn Discussion: Active conversations with the investor.\nSoft Circled: Verbal/informal commitment received.\nCommitted: Formal written commitment.\nDocs Signed: Legal documents executed.\nFunded: Money received in the bank.\nIssued: Shares/instruments formally issued.\nWithdrawn: Investor pulled out of the round.`}
      </CalcHelper>
      <div className="grid grid-cols-2 gap-3">
        <LabelVal label="Investor *">
          <Select value={form.holder_id || '_none'} onValueChange={v => set('holder_id', v === '_none' ? '' : v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select investor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">-- Select --</SelectItem>
              {holders.map(h => <SelectItem key={h.holder_id} value={h.holder_id}>{h.display_name || h.legal_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Status">
          <Select value={form.status || 'target'} onValueChange={v => set('status', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{COMMITMENT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
        <LabelVal label="Target Amount"><Input type="number" value={form.target_amount || ''} onChange={e => set('target_amount', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Committed Amount"><Input type="number" value={form.committed_amount || ''} onChange={e => set('committed_amount', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Funded Amount"><Input type="number" value={form.funded_amount || ''} onChange={e => set('funded_amount', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Expected Close Date"><Input type="date" value={form.expected_close_date || ''} onChange={e => set('expected_close_date', e.target.value)} className="h-8 text-sm" /></LabelVal>
        <LabelVal label="Instrument Type">
          <Select value={form.instrument_type || 'preferred_share'} onValueChange={v => set('instrument_type', v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{INSTRUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </LabelVal>
      </div>
      <LabelVal label="Notes"><Input value={form.notes || ''} onChange={e => set('notes', e.target.value)} className="h-8 text-sm" /></LabelVal>
    </div>
  );
}
